import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Trash2, ExternalLink } from "lucide-react";
import { AccountDialog, type SocialAccount } from "./AccountDialog";
import {
  PLATFORMS, FAMILY_MEMBERS, ACCOUNT_STATUSES, US_STATES, getPlatformColor, getPlatformLabel
} from "./constants";

export function AccountsPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SocialAccount | null>(null);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['social_accounts', filterPlatform, filterState, filterStatus, filterAssigned],
    queryFn: async () => {
      let q = supabase
        .from('social_accounts' as any)
        .select('*')
        .order('geo_type')
        .order('state_code')
        .order('platform');

      if (filterPlatform !== 'all') q = q.eq('platform', filterPlatform);
      if (filterState !== 'all') q = q.eq('state_code', filterState);
      if (filterStatus !== 'all') q = q.eq('status', filterStatus);
      if (filterAssigned !== 'all') q = q.eq('assigned_to', filterAssigned);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SocialAccount[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (account: SocialAccount) => {
      if (account.id) {
        const { id, ...updates } = account;
        const { error } = await supabase
          .from('social_accounts' as any)
          .update(updates)
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('social_accounts' as any)
          .insert([account]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_accounts'] });
      setDialogOpen(false);
      setEditingAccount(null);
      toast({ title: 'Account saved' });
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('social_accounts' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social_accounts'] });
      toast({ title: 'Account deleted' });
    },
    onError: (e: Error) => toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }),
  });

  const handleEdit = (account: SocialAccount) => {
    setEditingAccount(account);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingAccount(null);
    setDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const s = ACCOUNT_STATUSES.find(x => x.id === status);
    return s
      ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
      : <span className="text-muted-foreground text-xs">{status}</span>;
  };

  const getPlatformBadge = (platform: string) => {
    const color = getPlatformColor(platform);
    const label = getPlatformLabel(platform);
    const displayColor = color === '#000000' ? '#374151' : color;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: displayColor }}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Social Accounts</CardTitle>
              <CardDescription>
                {accounts.length} account{accounts.length !== 1 ? 's' : ''} — credential registry for all platforms
              </CardDescription>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add Account
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Select value={filterPlatform} onValueChange={setFilterPlatform}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Platforms" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger className="w-28"><SelectValue placeholder="All States" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {ACCOUNT_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterAssigned} onValueChange={setFilterAssigned}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Members" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {FAMILY_MEMBERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>Geo</TableHead>
                  <TableHead>Handle</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Followers</TableHead>
                  <TableHead>Last Post</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">Loading accounts...</TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No accounts found. Add your first account!
                    </TableCell>
                  </TableRow>
                ) : accounts.map((acct: any) => (
                  <TableRow key={acct.id}>
                    <TableCell>{getPlatformBadge(acct.platform)}</TableCell>
                    <TableCell className="text-sm">
                      {acct.geo_type === 'national'
                        ? <Badge variant="outline">National</Badge>
                        : acct.city_name
                        ? `${acct.city_name}`
                        : acct.state_code
                        ? <Badge variant="secondary">{acct.state_code}</Badge>
                        : '—'
                      }
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{acct.username ?? acct.handle ?? '—'}</div>
                      {acct.handle && acct.username && (
                        <div className="text-xs text-muted-foreground">{acct.handle}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{acct.email_used ?? '—'}</TableCell>
                    <TableCell className="text-sm">{acct.assigned_to ?? '—'}</TableCell>
                    <TableCell>{getStatusBadge(acct.status)}</TableCell>
                    <TableCell className="text-sm">
                      {acct.follower_count != null ? acct.follower_count.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {acct.last_post_at
                        ? new Date(acct.last_post_at).toLocaleDateString()
                        : '—'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {acct.profile_url && (
                          <Button size="sm" variant="ghost" asChild>
                            <a href={acct.profile_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(acct)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm('Delete this account?')) deleteMutation.mutate(acct.id);
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        account={editingAccount}
        onSave={data => saveMutation.mutate(data)}
        isPending={saveMutation.isPending}
      />
    </div>
  );
}
